'use client';

import { type ReactNode } from 'react';
import {
    PromoBar,
    type PromoBarsResponseType,
} from '@paypoteam/page-components';
import { ToastProvider } from '@paypoteam/ui-components';
import * as Sentry from '@sentry/nextjs';
import type { ImageProps } from 'next/image';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

import { bannerClickEvent } from '@/lib/gtm';
import { useCssDimensions } from '@/utils/hooks/useCssDimensions';

import Footer from '@/components/organisms/Footer';
import type { MobileAppBarProps } from '@/components/organisms/MobileAppBar';
import { MobileAppBar } from '@/components/organisms/MobileAppBar';
import {
    Navigation,
    type NavigationConfigType,
} from '@/components/organisms/Navigation';
import { Header } from '@/components/layout/Header';

export interface PageLayoutProps {
    children: ReactNode;
    promoBarData?: PromoBarsResponseType;
    mobileAppBarConfig?: MobileAppBarProps['config'];
    navigationConfig: NavigationConfigType;
}

export default function PageLayout({
    children,
    promoBarData,
    mobileAppBarConfig,
    navigationConfig,
}: PageLayoutProps) {
    const location = usePathname();
    const { minWidthMd } = useCssDimensions();

    Sentry.withScope(scope => scope.setTag('page-pathname', location));

    return (
        <>
            <ToastProvider />

            <Header>
                {promoBarData && (
                    <PromoBar<ImageProps>
                        promoBarData={promoBarData}
                        location={location}
                        promoBarClickEvent={() => {
                            bannerClickEvent();
                        }}
                        ImageComponent={Image}
                    />
                )}

                {mobileAppBarConfig && !minWidthMd && (
                    <MobileAppBar config={mobileAppBarConfig} />
                )}

                <Navigation config={navigationConfig} />
            </Header>

            <main>{children}</main>

            <Footer />
        </>
    );
}
